import { BRIDGE_CONFIG, BridgeAsset, FiatCurrency } from './bridgeConfig';
import { ensureSupabaseClientConfig } from '@/lib/env/runtime-env';

interface ExchangeRateResponse {
  base_asset: string;
  quote_asset: string;
  rate: string;
  midmarket_rate?: string;
  buy_rate?: string;
  sell_rate?: string;
  timestamp: string;
}

interface CachedRate {
  rate: number;
  timestamp: number;
  expiresAt: number;
}

// In-memory cache for exchange rates
const rateCache = new Map<string, CachedRate>();

/**
 * Get exchange rate between two currencies using Bridge API
 */
export async function getExchangeRate(
  fromAsset: BridgeAsset | FiatCurrency,
  toAsset: BridgeAsset | FiatCurrency
): Promise<number> {
  const cacheKey = `${fromAsset}-${toAsset}`;
  const now = Date.now();

  // Check cache first
  const cached = rateCache.get(cacheKey);
  if (cached && now < cached.expiresAt) {
    return cached.rate;
  }

  try {
    // Get anon key for Edge Function auth (required by Supabase)
    const { anonKey } = ensureSupabaseClientConfig();
    
    // Call our Edge function that proxies Bridge API
    const response = await fetch(
      `${BRIDGE_CONFIG.EXCHANGE_RATES_ENDPOINT}?base_asset=${fromAsset}&quote_asset=${toAsset}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey, // Required for Supabase Edge Functions
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Exchange rate API error: ${response.status}`);
    }

    const data: ExchangeRateResponse = await response.json();
    const rate = parseFloat(data.rate);

    if (isNaN(rate) || rate <= 0) {
      throw new Error('Invalid exchange rate received');
    }

    // Cache the rate
    rateCache.set(cacheKey, {
      rate,
      timestamp: now,
      expiresAt: now + BRIDGE_CONFIG.EXCHANGE_RATE_CACHE_DURATION,
    });

    return rate;

  } catch (error) {
    // Failed to fetch exchange rate from API

    // Return cached rate if available, even if expired
    if (cached) {
      // Using expired cached rate due to API error
      return cached.rate;
    }

    // Fallback rates for common pairs
    return getFallbackRate(fromAsset, toAsset);
  }
}

/**
 * Get multiple exchange rates in a single call
 */
export async function getMultipleExchangeRates(
  pairs: Array<{ from: BridgeAsset | FiatCurrency; to: BridgeAsset | FiatCurrency }>
): Promise<Record<string, number>> {
  const results: Record<string, number> = {};

  // Fetch rates in parallel
  const promises = pairs.map(async ({ from, to }) => {
    try {
      const rate = await getExchangeRate(from, to);
      results[`${from}-${to}`] = rate;
    } catch (error) {
      // Failed to fetch specific exchange rate
      results[`${from}-${to}`] = getFallbackRate(from, to);
    }
  });

  await Promise.all(promises);
  return results;
}

/**
 * Get fallback exchange rates for when API is unavailable
 */
function getFallbackRate(
  fromAsset: BridgeAsset | FiatCurrency,
  toAsset: BridgeAsset | FiatCurrency
): number {
  // Stablecoin to USD rates
  const stablecoinRates: Record<string, number> = {
    'USDC-USD': 1.0,
    'USDT-USD': 1.0,
    'PYUSD-USD': 1.0,
    'USDB-USD': 1.0,
    'DAI-USD': 1.0,
    'USDP-USD': 1.0,
  };

  // Crypto fallback rates removed (only stablecoins supported)

  // Euro rates
  const euroRates: Record<string, number> = {
    'EURC-EUR': 1.0,
    'EUR-USD': 1.08,
    'USD-EUR': 0.93,
  };

  const key = `${fromAsset}-${toAsset}`;

  // Check direct rates
  if (stablecoinRates[key]) return stablecoinRates[key];
  if (euroRates[key]) return euroRates[key];

  // Check reverse rates
  const reverseKey = `${toAsset}-${fromAsset}`;
  if (stablecoinRates[reverseKey]) return 1 / stablecoinRates[reverseKey];
  if (euroRates[reverseKey]) return 1 / euroRates[reverseKey];

  // Default fallback
  // No fallback rate available, using 1.0 as default
  return 1.0;
}

/**
 * Format exchange rate for display
 */
export function formatExchangeRate(
  rate: number,
  fromAsset: string,
  toAsset: string,
  precision: number = 4
): string {
  if (rate >= 1000) {
    return `1 ${fromAsset} = ${rate.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })} ${toAsset}`;
  }

  return `1 ${fromAsset} = ${rate.toFixed(precision)} ${toAsset}`;
}

/**
 * Check if exchange rate is stale (older than cache duration)
 */
export function isRateStale(fromAsset: string, toAsset: string): boolean {
  const cacheKey = `${fromAsset}-${toAsset}`;
  const cached = rateCache.get(cacheKey);

  if (!cached) return true;

  return Date.now() >= cached.expiresAt;
}

/**
 * Clear exchange rate cache
 */
export function clearRateCache(): void {
  rateCache.clear();
}

/**
 * Get cached rate without making API call
 */
export function getCachedRate(fromAsset: string, toAsset: string): number | null {
  const cacheKey = `${fromAsset}-${toAsset}`;
  const cached = rateCache.get(cacheKey);

  return cached ? cached.rate : null;
}